// File: apps/web/src/features/import/ImportPage.tsx
// Version: 2026.4.9 | Sprint 7 Wave 3 — in-flight banner + 409 handling
//
// CHANGE LOG (2026.4.9 Wave 3):
//   • Adds useInFlightImport() — fetches the current in-flight reservation
//     on mount and exposes setInFlightFromConflict / cancel.
//   • Renders <InFlightImportBanner /> persistently above the workflow
//     when an import is running. The banner offers Reconnect & Cancel.
//   • UploadZone is disabled while inFlight is non-null — server enforces
//     this anyway via the reservation index, but disabling at the client
//     makes the rejection visible before the user wastes time picking a file.
//   • Upload handler catches a 409 IMPORT_IN_FLIGHT response and feeds it
//     into setInFlightFromConflict, then surfaces a toast.
//   • Terminal SignalR events (completed/failed) trigger refresh() so the
//     banner clears as soon as the job ends.
//
// STATE MACHINE (preserved)
//
//   idle      → upload zone visible; staged list also visible (if any)
//   previewing → user picked a file, we have a preview; show PreviewCard
//   importing → POST to claude-export in flight
//   tracking  → import returned a batchId, SignalR is streaming progress
//   done      → batch completed; auto-flips back to idle so user can import more

import { useCallback, useEffect, useState } from 'react';
import EnginePageShell from '../../components/engine/EnginePageShell';
import { ENGINE_COLORS as C } from '../../components/engine/tokens';

import { UploadZone }            from './UploadZone';
import { PreviewCard }           from './PreviewCard';
import { ProgressStrip }         from './ProgressStrip';
import { StagedList }            from './StagedList';
import { StagedDetail }          from './StagedDetail';
import { CatalogHealthCard }     from './CatalogHealthCard';
import { ProposalsPanel }        from './ProposalsPanel';
import { ErrorPanel }            from '../../components/errors/ErrorPanel';
import { InFlightImportBanner }  from './InFlightImportBanner';
import { ENGINE_FONTS as F }     from '../../components/engine/tokens';
import { Tooltip, TooltipTitle, TooltipBody } from '../../components/engine/Tooltip';

import { useImportPreview, useImportClaudeExport, useProposals } from '../../lib/api/import';
import { useImportProgress }     from '../../lib/signalr/importHub';
import { useInFlightImport }     from '../../lib/api/useInFlightImport';
import type {
  StagedConversationResponse,
} from '../../lib/api/types-import';

type Phase = 'idle' | 'previewing' | 'importing' | 'tracking' | 'done';

export function ImportPage() {
  // ── Workflow state ─────────────────────────────────────────────────────
  const [phase,   setPhase]   = useState<Phase>('idle');
  const [file,    setFile]    = useState<File | null>(null);
  const [batchId, setBatchId] = useState<string | null>(null);

  // ── In-flight reservation ──────────────────────────────────────────────
  const {
    inFlight,
    cancel: cancelInFlight,
    setInFlightFromConflict,
    refresh: refreshInFlight,
  } = useInFlightImport();

  // ── List + drawer state ────────────────────────────────────────────────
  const [openStagedId,   setOpenStagedId]   = useState<number | null>(null);
  const [listRefreshKey, setListRefreshKey] = useState(0);

  // ── Tab selection (Conversations vs Proposals) ────────────────────────
  // Proposals live in their own panel because the review activity is
  // serially different from conversation mapping: accept proposals first
  // (which back-fills work item IDs onto contributing conversations),
  // then go back to the conversations tab to auto-commit. Keeping both
  // visible at once would force vertical scrolling past whichever panel
  // isn't the current focus.
  const [activeTab, setActiveTab] = useState<'conversations' | 'proposals'>('conversations');

  // ── API hooks ──────────────────────────────────────────────────────────
  const {
    preview,
    data:     previewData,
    loading:  previewLoading,
    envelope: previewEnvelope,
    reset:    resetPreview,
  } = useImportPreview();

  const {
    importExport,
    loading:  importLoading,
    envelope: importEnvelope,
  } = useImportClaudeExport();

  // ── SignalR ────────────────────────────────────────────────────────────
  // Subscribe to whichever batch is currently being tracked: either the
  // one we just kicked off, or the in-flight one we reconnected to via
  // the banner.
  const trackedBatchId =
    phase === 'tracking' || phase === 'done'
      ? batchId
      : null;

  const progress = useImportProgress(trackedBatchId);

  // Pending-proposals count drives the badge on the Proposals tab so the
  // reviewer can see at a glance whether the queue has work waiting
  // without switching tabs. The hook re-runs when refreshKey changes
  // (via its batchId dependency on the current tracked batch).
  const activeBatchFilter =
    phase === 'tracking' || phase === 'done' ? batchId ?? undefined : undefined;
  const { data: pendingProposals } = useProposals(
    { batchId: activeBatchFilter, status: 'Pending' },
    true);
  const pendingCount = pendingProposals?.total ?? 0;

  // When the batch finishes, refresh the staged list so newly-classified
  // rows show their AI suggestions, drop back to idle, and clear the
  // in-flight banner.
  useEffect(() => {
    if (progress.state === 'completed' || progress.state === 'failed') {
      setListRefreshKey(k => k + 1);
      void refreshInFlight(); // clears the banner
      const id = setTimeout(() => {
        setPhase('done');
      }, 1500);
      return () => clearTimeout(id);
    }
  }, [progress.state, refreshInFlight]);

  // ── Handlers ───────────────────────────────────────────────────────────

  const handleFileSelected = useCallback(async (picked: File) => {
    setFile(picked);
    setPhase('previewing');
    await preview(picked);
  }, [preview]);

  const handleConfirmImport = useCallback(async () => {
    if (!file) return;
    setPhase('importing');

    const outcome = await importExport(file);

    // null → some other error (the hook set `envelope`, ErrorPanel will render).
    if (!outcome) {
      setPhase('previewing');
      return;
    }

    // 409 — another import is already running for this tenant. Surface
    // it via the banner and bounce back to idle. The banner becomes the
    // user's only path forward (Reconnect or Cancel).
    if (outcome.conflict) {
      await setInFlightFromConflict(outcome.conflict);
      setPhase('idle');
      setFile(null);
      resetPreview();
      return;
    }

    // 202 — happy path. The dispatch result has batchId/jobId/statusUrl
    // but NOT newlyStaged — the async API doesn't know how many rows are
    // new until staging finishes. The "all duplicates → straight to idle"
    // path is now handled by the SignalR batchCompleted handler in the
    // useEffect below (it sees totalRows === 0 and clears the banner).
    if (outcome.result?.batchId) {
      setBatchId(outcome.result.batchId);
      setListRefreshKey(k => k + 1);
      setPhase('tracking');
      // Refresh in-flight so OUR upload populates the banner too,
      // covering second tabs that opened after our upload landed.
      void refreshInFlight();
    } else {
      setPhase('previewing');
    }
  }, [file, importExport, resetPreview, setInFlightFromConflict, refreshInFlight]);

  const handleCancelPreview = useCallback(() => {
    setFile(null);
    resetPreview();
    setPhase('idle');
  }, [resetPreview]);

  const handleStartOver = useCallback(() => {
    setFile(null);
    setBatchId(null);
    resetPreview();
    setPhase('idle');
  }, [resetPreview]);

  const handleReconnectInFlight = useCallback((reconnectBatchId: string) => {
    // Treat as if we had just uploaded — set the tracked batch and switch
    // to tracking phase. The SignalR hook will subscribe to the existing
    // group and start receiving rowClassified events for whatever rows
    // are still in flight.
    setBatchId(reconnectBatchId);
    setPhase('tracking');
    setListRefreshKey(k => k + 1);
  }, []);

  const handleRowClick = useCallback((row: StagedConversationResponse) => {
    setOpenStagedId(row.id);
  }, []);

  const handleDrawerClose = useCallback(() => {
    setOpenStagedId(null);
  }, []);

  const handleDrawerMutated = useCallback(() => {
    setListRefreshKey(k => k + 1);
  }, []);

  // ── Render ─────────────────────────────────────────────────────────────

  // The upload zone is disabled when an in-flight job exists OR when we're
  // mid-import. The banner is the user's only path forward in those states.

  return (
    <EnginePageShell
      title="Import"
      subtitle="CONVERSATION INGEST"
      icon="📥"
      accentColor={C.cyan}
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 24, padding: '0 24px 48px' }}>
        {/* ─── Persistent in-flight banner ──────────────────────────── */}
        {inFlight && (
          <InFlightImportBanner
            job={inFlight}
            onReconnect={handleReconnectInFlight}
            onCancel={cancelInFlight}
          />
        )}

        {/* ─── Active workflow ──────────────────────────────────────── */}

        {phase === 'idle' && !inFlight && (
          <UploadZone
            onFileSelected={handleFileSelected}
            hint="Settings → Privacy → Export data on claude.ai. Drop the .zip here."
          />
        )}

        {phase === 'previewing' && previewLoading && (
          <UploadZone onFileSelected={handleFileSelected} busy hint="Parsing your export…" />
        )}

        {phase === 'previewing' && !previewLoading && previewData && (
          <PreviewCard
            preview={previewData}
            onConfirm={handleConfirmImport}
            onCancel={handleCancelPreview}
            busy={importLoading}
          />
        )}

        {phase === 'previewing' && !previewLoading && previewEnvelope && (
          <ErrorPanel
            error={previewEnvelope.error}
            onRetry={handleCancelPreview}
          />
        )}

        {phase === 'importing' && file && (
          <UploadZone onFileSelected={handleFileSelected} busy hint={`Uploading ${file.name}…`} />
        )}

        {phase === 'importing' && importEnvelope && (
          <ErrorPanel
            error={importEnvelope.error}
            onRetry={handleCancelPreview}
          />
        )}

        {(phase === 'tracking' || phase === 'done') && batchId && (
          <>
            <ProgressStrip progress={progress} batchId={batchId} />
            {phase === 'done' && (
              <div style={{ display: 'flex', justifyContent: 'center' }}>
                <button onClick={handleStartOver} style={importMoreButtonStyle}>
                  Import another export →
                </button>
              </div>
            )}
          </>
        )}

        {/* ─── Catalog health warnings ─────────────────────────────── */}
        <CatalogHealthCard />

        {/* ─── Tab strip ───────────────────────────────────────────── */}
        {/*
            The two review surfaces serve different activities:
              • Conversations — map rows to work items, accept AI
                suggestions, override R&D verdicts, commit sessions.
              • Proposals     — review AI-proposed new work items, accept
                to create catalog entries, reject, edit, merge.
            Accepting proposals back-fills AISuggestedWorkItemId on
            contributing conversations, which then flow through the
            Conversations tab's normal auto-commit path. Users typically
            work Proposals first (creates catalog entries), then
            Conversations (maps everything and commits).
        */}
        <div style={tabStripStyle}>
          <Tooltip
            placement="bottom"
            maxWidth={320}
            content={
              <>
                <TooltipTitle>📋 Conversations</TooltipTitle>
                <TooltipBody>
                  Per-conversation review: map rows to work items, accept
                  AI suggestions, override R&amp;D verdicts, commit
                  sessions. This is the main operational surface.
                </TooltipBody>
              </>
            }
          >
            <button
              onClick={() => setActiveTab('conversations')}
              style={tabButtonStyle(activeTab === 'conversations', C.cyan)}
            >
              📋 Conversations
            </button>
          </Tooltip>
          <Tooltip
            placement="bottom"
            maxWidth={340}
            content={
              <>
                <TooltipTitle>⚡ Proposals</TooltipTitle>
                <TooltipBody>
                  Review AI-proposed work items that don't match any
                  existing catalog entry. Accept to create a real WorkItem
                  (which auto-maps every contributing conversation);
                  reject to leave contributions in the Unmapped queue; or
                  merge near-duplicate proposals together.
                </TooltipBody>
              </>
            }
          >
            <button
              onClick={() => setActiveTab('proposals')}
              style={tabButtonStyle(activeTab === 'proposals', C.violet)}
            >
              ⚡ Proposals
              {pendingCount > 0 && (
                <span style={tabBadgeStyle(activeTab === 'proposals')}>
                  {pendingCount}
                </span>
              )}
            </button>
          </Tooltip>
        </div>

        {/* ─── Active tab surface ──────────────────────────────────── */}

        {activeTab === 'conversations' && (
          <StagedList
            onRowClick={handleRowClick}
            batchFilter={activeBatchFilter}
            refreshKey={listRefreshKey}
          />
        )}

        {activeTab === 'proposals' && (
          <ProposalsPanel
            batchFilter={activeBatchFilter}
            refreshKey={listRefreshKey}
            onMutated={handleDrawerMutated}
          />
        )}
      </div>

      {/* ─── Drawer ──────────────────────────────────────────────────── */}

      {openStagedId != null && (
        <StagedDetail
          stagedId={openStagedId}
          onClose={handleDrawerClose}
          onMutated={handleDrawerMutated}
        />
      )}
    </EnginePageShell>
  );
}

// ─── Styles ────────────────────────────────────────────────────────────────

const importMoreButtonStyle: React.CSSProperties = {
  padding:      '12px 24px',
  fontSize:     14,
  fontWeight:   600,
  background:   'transparent',
  color:        C.cyan,
  border:       `1px solid ${C.cyan}58`,
  borderRadius: 8,
  cursor:       'pointer',
};

const tabStripStyle: React.CSSProperties = {
  display:    'flex',
  gap:        8,
  alignItems: 'center',
};

const tabButtonStyle = (active: boolean, color: string): React.CSSProperties => ({
  display:       'inline-flex',
  alignItems:    'center',
  gap:           8,
  padding:       '10px 18px',
  fontSize:      13,
  fontWeight:    600,
  fontFamily:    F.display,
  letterSpacing: '0.02em',
  color:         active ? color : C.muted,
  background:    active ? `${color}14` : 'transparent',
  border:        `1px solid ${active ? color + '55' : C.faint}`,
  borderRadius:  8,
  cursor:        'pointer',
  transition:    'all 0.15s ease',
});

const tabBadgeStyle = (active: boolean): React.CSSProperties => ({
  fontSize:     10,
  fontWeight:   700,
  fontFamily:   F.mono,
  color:        active ? '#050910' : C.violet,
  background:   active ? C.violet : `${C.violet}20`,
  border:       `1px solid ${active ? C.violet : C.violet + '55'}`,
  borderRadius: 10,
  padding:      '1px 7px',
  minWidth:     18,
  textAlign:    'center',
});
